import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
// import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries"; // Store Custom Code
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { COLORS, COMMON, SPACING, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { useEffect } from "react";
/* Store Custom Code - End */

const enum fields {
    showImages = "showImages",
}

/* Store Custom Code - Start */
const mapDispatchToProps = { loadCategories };

interface Props extends WidgetProps, HasCategoryContext {
    loadCategories: any;
}
/* Store Custom Code - End */

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // Store Custom Code
    subcategoriesContainer?: GridContainerProps;
    secondSubCategoryNameLinkItem?: GridItemProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
    },
    subCategoryItem: {
        /* Store Custom Code - Start */
        width: [12, 12, 4, 3, 3],
        css: css`
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [30, null, 40, 60, null], properties: ["padding-bottom"], unit: "px" }],
                    mediaOptions.min,
                )}
        `,
        /* Store Custom Code - End */
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [2, 2, 12, 12, 12],
        /* Store Custom Code - Start */
        css: css`
            width: 100%;
            padding: 0 15px 15px;
        `,
        /* Store Custom Code - End */
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                max-width: 150px; /* Store Custom Code */
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        /* Store Custom Code - Start */
        css: css`
            align-items: center;
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: [null, null, "center", null, null], properties: ["justify-content"] },
                        { values: ["0 15px 15px", null, "0 15px 25px", null, null], properties: ["padding"] },
                    ],
                    mediaOptions.min,
                )}
        `,
        /* Store Custom Code - End */
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        /* Store Custom Code - Start */
        typographyProps: {
            size: 18,
            weight: TYPOGRAPHY.weight.black,
            variant: "h2",
            css: css`
                color: ${COLORS.secondary.base} !important;
                width: 100%;
                line-height: ${TYPOGRAPHY.lineHeight.small};
                text-align: left;
                word-wrap: break-word;
                text-transform: uppercase;
                font-size: ${TYPOGRAPHY.size.large};

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: [null, null, "66px", null, null], properties: ["min-height"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        /* Store Custom Code - End */
    },
    // Store Custom Code
    subcategoriesContainer: {
        gap: 0,
        css: css`
            width: 100%;
            margin: 0;
        `,
    },
    secondSubCategoryNameLinkItem: {
        width: 12,
        css: css`
            position: relative;
            padding: ${SPACING.standard} ${SPACING.medium} ${SPACING.standard} 0;
            border-bottom: 1px solid ${COLORS.border.base};
            margin: 0 15px !important;

            &::before {
                content: "";
                display: block;
                position: absolute;
                right: 0;
                top: 50%;
                width: 7px;
                height: 7px;
                border-top: 2px solid ${COLORS.primary.base};
                border-right: 2px solid ${COLORS.primary.base};
                transform: translateY(-50%) rotate(45deg);
            }

            &:first-child {
                border-top: 1px solid ${COLORS.border.base};
            }
        `,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            padding: 25px 15px 0;
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["flex-end", null, "flex-start", null, null], properties: ["justify-content"] }],
                    mediaOptions.min,
                )}
        `,
    },
    viewMoreLink: {
        typographyProps: {
            size: 14,
            css: css`
                text-decoration: underline;
            `,
        },
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({
    category,
    fields: { showImages },
    // Store Custom Code
    loadCategories,
}: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    /* Store Custom Code - Start */
    useEffect(() => {
        loadCategories(category?.id, 1);
    }, []);
    /* Store Custom Code - End */

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    const history = useHistory(); // Store Custom Code

    if (!category) {
        return null;
    }

    /* Store Custom Code - Start */
    const goToUrl = (link: string) => {
        return category.properties.redirectUrl !== undefined ? window.open(link, "_blank") : history.push(link);
    };
    /* Store Custom Code - End */

    return (
        <GridContainer {...styles.innerContainer}>
            {showImages && (
                <GridItem {...styles.subCategoryImageItem}>
                    {/* Store Custom Code - Start */}
                    <Clickable onClick={() => goToUrl(category.path)} {...styles.subCategoryImageClickable}>
                        <LazyImage
                            src={category.smallImagePath || COMMON.defaultCategoryImagePath}
                            altText={category.imageAltText}
                            {...styles.subCategoryImage}
                        />
                    </Clickable>
                    {/* Store Custom Code - End */}
                </GridItem>
            )}
            <GridItem {...styles.subCategoryNameLinkItem}>
                <Link
                    onClick={() => goToUrl(category.path)} // Store Custom Code
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Link>
            </GridItem>
            {/* Store Custom Code - Removed subcategoriesContainer */}
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(CategoryDetailSubCategories)), // Store Custom Code
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
